import React, { useMemo } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import FiltersSidebar, {
  FilterSection,
  FilterCheckboxRow,
  FilterCheckboxList,
} from './FiltersSidebar';
import type { ProjectStatus } from '../services/projectService';
import type { ProjectFilterRow, UseProjectFiltersResult } from '../hooks/useProjectFilters';

interface ProjectFiltersSidebarProps {
  filters: UseProjectFiltersResult;
  /** Source list — used to derive per-status counts shown in the right column
   *  of each checkbox row. Counts are over the full loaded set, not the
   *  post-filter set, so they stay stable as you toggle. */
  rows: ProjectFilterRow[];
  collapsed: boolean;
}

// Date inputs use plain HTML <input type="date"> — matches OrderFiltersSidebar
// (Fluent v9 ships no date picker in the set we're on). Styling kept minimal so
// they read as part of the sidebar surface.
const useStyles = makeStyles({
  dateRow: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
  },
  dateLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
  },
  dateInput: {
    width: '100%',
    boxSizing: 'border-box',
    ...shorthands.padding('6px', '8px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    fontSize: '12px',
    fontFamily: 'inherit',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    '&:focus': {
      outline: 'none',
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
  },
});

// Project status vocabulary — mirrors ProjectStatusBadge's STATUS_META so the
// filter labels match the badges in the table.
const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
];

/**
 * Left-rail filter panel for the Projects table. Same shell and live-apply
 * behavior as OrderFiltersSidebar; state persists via useProjectFilters.
 */
const ProjectFiltersSidebar: React.FC<ProjectFiltersSidebarProps> = ({
  filters,
  rows,
  collapsed,
}) => {
  const styles = useStyles();
  const { state, setField, clearAll, activeFilterCount, hasActiveFilters } = filters;

  const statusCounts = useMemo(() => {
    const s = new Map<ProjectStatus, number>();
    for (const r of rows) {
      s.set(r.status, (s.get(r.status) || 0) + 1);
    }
    return s;
  }, [rows]);

  const toggleStatus = (s: ProjectStatus) => {
    const has = state.statuses.includes(s);
    setField(
      'statuses',
      has ? state.statuses.filter((x) => x !== s) : [...state.statuses, s],
    );
  };

  return (
    <FiltersSidebar
      collapsed={collapsed}
      activeFilterCount={activeFilterCount}
      hasActiveFilters={hasActiveFilters}
      onClearAll={clearAll}
      onRequestClose={() => filters.setField('sidebarCollapsed', true)}
    >
      <FilterSection label="Status">
        <FilterCheckboxList>
          {STATUS_OPTIONS.map((opt) => (
            <FilterCheckboxRow
              key={opt.value}
              checked={state.statuses.includes(opt.value)}
              onChange={() => toggleStatus(opt.value)}
              label={opt.label}
              count={statusCounts.get(opt.value) ?? 0}
            />
          ))}
        </FilterCheckboxList>
      </FilterSection>

      <FilterSection label="Created">
        <div className={styles.dateRow}>
          <span className={styles.dateLabel}>From</span>
          <input
            type="date"
            className={styles.dateInput}
            value={state.dateFrom}
            onChange={(e) => setField('dateFrom', e.target.value)}
          />
        </div>
        <div className={styles.dateRow}>
          <span className={styles.dateLabel}>Until</span>
          <input
            type="date"
            className={styles.dateInput}
            value={state.dateUntil}
            onChange={(e) => setField('dateUntil', e.target.value)}
          />
        </div>
      </FilterSection>
    </FiltersSidebar>
  );
};

export default ProjectFiltersSidebar;
